/**
 * CustomersReport (LISTS-CUSTOMERS) — the real audience builder. Every customer
 * in one filterable list (geography, plan status, has-email, balance, active)
 * off the `customer_directory` view; save your own views; export CSV; and
 * "Email these customers" sends a personalized template to the WHOLE filtered
 * list (suppression-aware, gated by the master comms switch). Replaces the
 * three hard-coded marketing segments — Seth: "I want to build whatever
 * audience I want, not predefined ones I can't edit."
 *
 * Competitor frame: HubSpot active lists + ServiceTitan's customer list with
 * bulk actions — filter to a set, act on the set. Adopted. The filters that
 * need precomputed metrics (last service, lifetime value) wait on
 * customer_metrics being repopulated; geography + plan + email + balance are
 * derived live from the mirror and cover the common marketing audiences.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Users, Mail, X, ArrowRight, Megaphone } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { displayCustomerName } from "@/lib/format";
import { ListSearch, ViewsMenu, pillInputCls, pillSelectCls, CsvButton, downloadCsv, useSort, useColumns, ColumnsButton, ListTable, FiltersButton, FilterChips, applyFilters, type ListField, type FilterCond } from "./listCore";
import { EmailAudienceModal, type AudienceRecipient } from "@/features/comms/EmailAudienceModal";
import { useNavigateWithBack } from "@/lib/navBack";

interface DirRow {
  custno: string;
  name: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone1: string | null;
  email: string | null;
  has_email: boolean;
  plan_status: "member" | "lapsed" | "none";
  has_balance: boolean;
  inactive: boolean;
  firstname: string | null;
  lastname: string | null;
  address2: string | null;
  phone2: string | null;
  phone3: string | null;
  phone4: string | null;
  date_added: string | null;
  date_modified: string | null;
  locname: string | null;
  zone: string | null;
  contact: string | null;
  email2: string | null;
}

interface Filters {
  search: string;
  city: string;
  state: string;
  zip: string;
  plan: "any" | "member" | "lapsed" | "none";
  email: "any" | "has" | "none";
  balance: "any" | "owes" | "clear";
  activeOnly: boolean;
}

const EMPTY: Filters = { search: "", city: "", state: "", zip: "", plan: "any", email: "any", balance: "any", activeOnly: true };
const DISPLAY_CAP = 500;
const COLS = "custno,name,address,city,state,zip,phone1,email,has_email,plan_status,has_balance,inactive,firstname,lastname,address2,phone2,phone3,phone4,date_added,date_modified,locname,zone,contact,email2";

export function CustomersReport() {
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [conds, setConds] = useState<FilterCond[]>([]);
  const [emailing, setEmailing] = useState(false);
  const [selected, setSelected] = useState<DirRow | null>(null);
  const navigate = useNavigateWithBack();
  const sort = useSort<DirRow>("name", "asc");

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["customer_directory", filters, conds],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<{ rows: DirRow[]; count: number }> => {
      let q = supabase.from("customer_directory").select(COLS, { count: "exact" });
      if (filters.activeOnly) q = q.eq("inactive", false);
      if (filters.plan !== "any") q = q.eq("plan_status", filters.plan);
      if (filters.email === "has") q = q.eq("has_email", true);
      if (filters.email === "none") q = q.eq("has_email", false);
      if (filters.balance === "owes") q = q.eq("has_balance", true);
      if (filters.balance === "clear") q = q.eq("has_balance", false);
      if (filters.city.trim()) q = q.ilike("city", `%${filters.city.trim()}%`);
      if (filters.state.trim()) q = q.ilike("state", filters.state.trim());
      if (filters.zip.trim()) q = q.ilike("zip", `${filters.zip.trim()}%`);
      const s = filters.search.replace(/[%,()*]/g, " ").trim();
      if (s) q = q.or(`name.ilike.*${s}*,custno.ilike.*${s}*,city.ilike.*${s}*`);
      q = applyFilters(q, conds, fields as ListField<unknown>[]); // any-field filters
      const { data, count, error } = await q.order("name").limit(DISPLAY_CAP);
      if (error) throw error;
      return { rows: (data ?? []) as DirRow[], count: count ?? 0 };
    },
  });

  const count = data?.count ?? 0;
  const rows = data?.rows ?? [];

  const planChip = (p: DirRow["plan_status"]) =>
    p === "member" ? <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700">Member</span>
      : p === "lapsed" ? <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700">Lapsed</span>
        : <span className="text-[11px] text-slate-400">—</span>;

  // The Customers field catalog — choose columns, group by city/plan/etc., in
  // the Columns menu. The same shape every entity list (and Ask Pegasus) uses.
  const fields: ListField<DirRow>[] = useMemo(() => [
    {
      id: "name", label: "Customer", value: (r) => r.name,
      render: (r) => (
        <span>
          <span className="font-medium text-slate-800">{displayCustomerName(null, r.name) || r.custno}</span>
          {r.has_balance && <span className="ml-1.5 rounded bg-red-50 px-1 py-0.5 text-[10px] font-semibold text-red-600">owes</span>}
          {r.inactive && <span className="ml-1.5 rounded bg-slate-100 px-1 py-0.5 text-[10px] font-semibold text-slate-400">inactive</span>}
        </span>
      ),
    },
    { id: "address", type: "text", filterable: true, label: "Address", value: (r) => r.address },
    { id: "city", type: "text", filterable: true, label: "City", value: (r) => r.city, groupable: true },
    { id: "state", type: "text", filterable: true, label: "State", value: (r) => r.state, groupable: true },
    { id: "zip", type: "text", filterable: true, label: "Zip", value: (r) => r.zip, groupable: true },
    { id: "plan_status", type: "enum", filterable: true, options: [{ value: "member", label: "Member" }, { value: "lapsed", label: "Lapsed" }, { value: "none", label: "No plan" }], label: "Plan", value: (r) => r.plan_status, groupable: true, render: (r) => planChip(r.plan_status) },
    { id: "email", type: "text", filterable: true, label: "Email", value: (r) => r.email, render: (r) => r.email ?? <span className="text-slate-300">none</span> },
    { id: "phone1", type: "text", filterable: true, label: "Phone", value: (r) => r.phone1, defaultVisible: false },
    { id: "custno", type: "text", filterable: true, label: "Cust #", value: (r) => r.custno, defaultVisible: false },
    { id: "has_balance", type: "bool", filterable: true, label: "Owes", value: (r) => (r.has_balance ? "Owes" : ""), groupable: true, defaultVisible: false },
    { id: "has_email", type: "bool", filterable: true, label: "Has Email", value: (r) => (r.has_email ? "Yes" : "No"), groupable: true, defaultVisible: false },
    { id: "inactive", type: "bool", filterable: true, col: "inactive", label: "Inactive", value: (r) => (r.inactive ? "Yes" : "No"), defaultVisible: false },
    { id: "active", label: "Status", value: (r) => (r.inactive ? "Inactive" : "Active"), groupable: true, defaultVisible: false },
    { id: "firstname", type: "text", filterable: true, label: "First Name", value: (r) => r.firstname, defaultVisible: false },
    { id: "lastname", type: "text", filterable: true, label: "Last Name", value: (r) => r.lastname, defaultVisible: false },
    { id: "locname", type: "text", filterable: true, label: "Location / Business", value: (r) => r.locname, defaultVisible: false },
    { id: "contact", type: "text", filterable: true, label: "Contact", value: (r) => r.contact, defaultVisible: false },
    { id: "address2", type: "text", filterable: true, label: "Address 2", value: (r) => r.address2, defaultVisible: false },
    { id: "zone", type: "text", filterable: true, label: "Zone", value: (r) => r.zone, groupable: true, defaultVisible: false },
    { id: "phone2", type: "text", filterable: true, label: "Phone 2", value: (r) => r.phone2, defaultVisible: false },
    { id: "phone3", type: "text", filterable: true, label: "Phone 3", value: (r) => r.phone3, defaultVisible: false },
    { id: "phone4", type: "text", filterable: true, label: "Phone 4", value: (r) => r.phone4, defaultVisible: false },
    { id: "email2", type: "text", filterable: true, label: "Email 2", value: (r) => r.email2, defaultVisible: false },
    { id: "date_added", type: "date", filterable: true, label: "Date Added", value: (r) => (r.date_added ? r.date_added.slice(0, 10) : null), defaultVisible: false },
    { id: "date_modified", type: "date", filterable: true, label: "Date Modified", value: (r) => (r.date_modified ? r.date_modified.slice(0, 10) : null), defaultVisible: false },
  ], [navigate]);
  const cols = useColumns("customers", fields);

  // Resolve the FULL filtered audience to emailable recipients (has_email),
  // deduped by address — the same filters the table is showing.
  async function resolveRecipients(): Promise<AudienceRecipient[]> {
    const f = filters;
    let q = supabase.from("customer_directory").select("custno,email").eq("has_email", true);
    if (f.activeOnly) q = q.eq("inactive", false);
    if (f.plan !== "any") q = q.eq("plan_status", f.plan);
    if (f.balance === "owes") q = q.eq("has_balance", true);
    if (f.balance === "clear") q = q.eq("has_balance", false);
    if (f.city.trim()) q = q.ilike("city", `%${f.city.trim()}%`);
    if (f.state.trim()) q = q.ilike("state", f.state.trim());
    if (f.zip.trim()) q = q.ilike("zip", `${f.zip.trim()}%`);
    const s = f.search.replace(/[%,()*]/g, " ").trim();
    if (s) q = q.or(`name.ilike.*${s}*,custno.ilike.*${s}*,city.ilike.*${s}*`);
    q = applyFilters(q, conds, fields as ListField<unknown>[]); // any-field filters apply to the email audience too
    const { data } = await q.order("custno").limit(1000);
    const seen = new Set<string>();
    const out: AudienceRecipient[] = [];
    for (const r of ((data ?? []) as { custno: string; email: string | null }[])) {
      const e = (r.email ?? "").trim();
      if (e && !seen.has(e)) { seen.add(e); out.push({ custno: r.custno, email: e }); }
    }
    return out;
  }

  const labelBits = [
    filters.plan === "member" ? "Members" : filters.plan === "lapsed" ? "Lapsed members" : filters.plan === "none" ? "No plan" : null,
    filters.city.trim() || null,
    filters.state.trim() ? filters.state.trim().toUpperCase() : null,
    filters.zip.trim() ? `zip ${filters.zip.trim()}` : null,
    filters.balance === "owes" ? "owes a balance" : filters.balance === "clear" ? "paid up" : null,
    filters.search.trim() ? `"${filters.search.trim()}"` : null,
  ].filter(Boolean);
  const audienceLabel = labelBits.length ? labelBits.join(" · ") : (filters.activeOnly ? "All active customers" : "All customers");
  // MKT M2: promote this report's filters to a saved AUDIENCE in Marketing
  // (plan, city, ZIP, email, balance, active) — the any-field conditions and
  // the search box don't translate, so they're named in the audience's note.
  const saveAsAudience = () => {
    const spec: Record<string, unknown> = {};
    if (filters.plan !== "any") spec.plan = filters.plan;
    if (filters.city.trim()) spec.city = [filters.city.trim()];
    if (filters.zip.trim()) spec.zip = [filters.zip.trim()];
    if (filters.email === "has") spec.has_email = true;
    if (filters.balance === "clear") spec.exclude_balance = true;
    if (!filters.activeOnly) spec.active_only = false;
    navigate(`/marketing?new_audience=${encodeURIComponent(JSON.stringify({ name: audienceLabel, spec }))}`);
  };
  const dirty = filters.search || filters.city || filters.state || filters.zip || filters.plan !== "any" || filters.email !== "any" || filters.balance !== "any" || !filters.activeOnly;

  // CSV mirrors what's on screen — the chosen columns, in order (ESC: export
  // what you see).
  function exportCsv() {
    const shown = cols.visible.map((id) => fields.find((f) => f.id === id)).filter(Boolean) as ListField<DirRow>[];
    downloadCsv(
      `customers-${new Date().toISOString().slice(0, 10)}.csv`,
      shown.map((f) => f.label),
      rows.map((r) => shown.map((f) => { const v = f.value(r); return v == null ? "" : String(v); })),
    );
  }

  return (
    <div className="min-w-0 flex-1">
      {/* TOOLBAR-REFRESH — search-forward command row + a scoping row of pills.
          No date range (the audience is geography/plan/balance, not a period). */}
      <div className="mb-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <ListSearch value={filters.search} onChange={(v) => setFilters({ ...filters, search: v })} placeholder="Search name, customer #, city…" />
          <FiltersButton fields={fields} conds={conds} onChange={setConds} />
          <ColumnsButton fields={fields} cols={cols} />
          <CsvButton onClick={exportCsv} />
          <ViewsMenu entity="customers" currentFilters={filters as unknown as Record<string, unknown>} onApply={(f) => setFilters({ ...EMPTY, ...(f as Partial<Filters>) })} />
          <button onClick={() => setEmailing(true)} disabled={count === 0} title="Email a template to this whole filtered list" className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-brand-600 px-3 text-xs font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-50">
            <Mail className="h-3.5 w-3.5" /> Email these{count > 0 ? ` (${count.toLocaleString()})` : ""}
          </button>
          <button onClick={saveAsAudience} title="Save these filters as a Marketing audience (plan, city, ZIP, email, balance, active)" className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-hairline bg-white px-3 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50">
            <Megaphone className="h-3.5 w-3.5" /> Save as audience
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} placeholder="City" className={cn(pillInputCls, "w-28")} />
          <input value={filters.state} onChange={(e) => setFilters({ ...filters, state: e.target.value })} placeholder="State" className={cn(pillInputCls, "w-16")} />
          <input value={filters.zip} onChange={(e) => setFilters({ ...filters, zip: e.target.value })} placeholder="Zip" className={cn(pillInputCls, "w-20")} />
          <select value={filters.plan} onChange={(e) => setFilters({ ...filters, plan: e.target.value as Filters["plan"] })} className={pillSelectCls} title="Plan status">
            <option value="any">Any plan</option><option value="member">Members</option><option value="lapsed">Lapsed</option><option value="none">No plan</option>
          </select>
          <select value={filters.email} onChange={(e) => setFilters({ ...filters, email: e.target.value as Filters["email"] })} className={pillSelectCls} title="Email on file">
            <option value="any">Any email</option><option value="has">Has email</option><option value="none">No email</option>
          </select>
          <select value={filters.balance} onChange={(e) => setFilters({ ...filters, balance: e.target.value as Filters["balance"] })} className={pillSelectCls} title="Balance">
            <option value="any">Any balance</option><option value="owes">Owes</option><option value="clear">Paid up</option>
          </select>
          <label className="flex h-9 cursor-pointer items-center gap-1.5 rounded-lg border border-hairline bg-white px-3 text-xs font-medium text-slate-600">
            <input type="checkbox" checked={filters.activeOnly} onChange={(e) => setFilters({ ...filters, activeOnly: e.target.checked })} className="h-3.5 w-3.5 accent-brand-700" /> Active only
          </label>
          {dirty && <button onClick={() => setFilters(EMPTY)} className="text-xs font-medium text-slate-400 hover:text-slate-600">Clear</button>}
          {conds.length > 0 && <FilterChips conds={conds} fields={fields} onChange={setConds} />}
          <div className="ml-auto whitespace-nowrap text-sm tabular-nums text-slate-500">
            <strong className="text-slate-800">{count.toLocaleString()}</strong> customers{isFetching ? " · …" : ""}{count > DISPLAY_CAP ? ` · first ${DISPLAY_CAP}` : ""}
          </div>
        </div>
      </div>

      {isLoading ? (
        <Card className="h-40 animate-pulse bg-slate-100" />
      ) : (
        <div className="flex items-start gap-3">
          <Card className="min-w-0 flex-1 overflow-hidden">
            <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2.5">
              <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-brand-50 text-brand-600"><Users className="h-3.5 w-3.5" /></span>
              <span className="text-[14.5px] font-bold tracking-tight text-slate-900">All Customers</span>
              <InfoTip label="How this list works">
                Click a row to see details. “Email these” sends a personalized template to everyone in the filtered list who has an email;
                unsubscribed customers are skipped. Save your filters as a view to reuse them.
              </InfoTip>
              <span className="ml-auto text-xs tabular-nums text-slate-500">
                {count.toLocaleString()} match{isFetching ? " · …" : ""}{count > DISPLAY_CAP ? ` · showing first ${DISPLAY_CAP}` : ""}
              </span>
            </div>
            <ListTable
              rows={rows}
              fields={fields}
              cols={cols}
              sort={sort}
              getKey={(r) => r.custno}
              onRowClick={(r) => setSelected(r)}
              selectedKey={selected?.custno ?? null}
              emptyText="No customers match these filters."
            />
          </Card>
          {selected && (
            <CustomerDetailsPanel
              row={selected}
              onClose={() => setSelected(null)}
              onOpen={() => navigate(`/customers/${selected.custno}`)}
              onOpenTab={(tab) => navigate(`/customers/${selected.custno}?tab=${tab}`)}
            />
          )}
        </div>
      )}

      {emailing && (
        <EmailAudienceModal audienceLabel={audienceLabel} resolveRecipients={resolveRecipients} onClose={() => setEmailing(false)} />
      )}
    </div>
  );
}

// The record peek panel (ESC's right-hand Details pane, two-window model: a row
// click peeks here; "Open the 360" is the deliberate drill). Reusable shape for
// every entity list — the content is the only entity-specific part.
function CustomerDetailsPanel({ row, onClose, onOpen, onOpenTab }: { row: DirRow; onClose: () => void; onOpen: () => void; onOpenTab: (tab: string) => void }) {
  const planLabel = row.plan_status === "member" ? "Plan member" : row.plan_status === "lapsed" ? "Lapsed member" : "No plan";
  const cityLine = [row.city, row.state].filter(Boolean).join(", ");
  const LINKS: [string, string][] = [["dispatches", "Service"], ["equipment", "Equipment"], ["quotes", "Quotes"], ["agreements", "Agreements"], ["invoices", "Invoices"]];
  return (
    <div className="w-72 shrink-0">
      <Card className="overflow-hidden">
        <div className="flex items-start justify-between gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-3">
          <div className="min-w-0">
            <div className="truncate text-sm font-bold text-slate-900">{displayCustomerName(null, row.name) || row.custno}</div>
            <div className="text-[11px] text-slate-400">{row.custno}</div>
          </div>
          <button onClick={onClose} title="Close" className="shrink-0 text-slate-300 transition-colors hover:text-slate-600"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3 p-4">
          {(row.address || cityLine) && (
            <div className="text-sm leading-snug text-slate-700">
              {row.address && <div>{row.address}</div>}
              {(cityLine || row.zip) && <div className="text-slate-500">{cityLine}{row.zip ? ` ${row.zip}` : ""}</div>}
            </div>
          )}
          {row.phone1 && <a href={`tel:${row.phone1}`} className="block text-sm font-medium text-brand-700 hover:underline">{row.phone1}</a>}
          {row.email && <div className="truncate text-sm text-slate-600">{row.email}</div>}
          <div className="flex flex-wrap gap-1.5 pt-0.5">
            <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold", row.plan_status === "member" ? "bg-emerald-50 text-emerald-700" : row.plan_status === "lapsed" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500")}>{planLabel}</span>
            {row.has_balance && <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-600">Owes a balance</span>}
            {row.inactive && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-400">Inactive</span>}
          </div>
          <div className="border-t border-slate-100 pt-3">
            <div className="mb-1.5 text-[12px] font-semibold text-slate-500">Jump to</div>
            <div className="flex flex-wrap gap-1.5">
              {LINKS.map(([tab, label]) => (
                <button key={tab} onClick={() => onOpenTab(tab)} className="rounded-md border border-hairline bg-white px-2 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700">
                  {label}
                </button>
              ))}
            </div>
          </div>
          <button onClick={onOpen} className="mt-1 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-brand-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-brand-700">
            Open the 360 <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </Card>
    </div>
  );
}
